import { format } from 'date-fns/format';
import { buildCLevelAnnualDetailPath, buildCLevelTamMonthlyDetailPath } from './annualPrime.utils';
import { isValidMonthYear, parseMonthlyPrimeTamSearchParam } from './monthlyPrime.utils';

export type PrimesPeriodView = 'monthly' | 'annual';

export type PrimesPeriodToggleResolvedProps = {
  value: PrimesPeriodView;
  monthlyTo: string;
  annualTo: string;
};

export type PrimesPeriodToggleProps = {
  activeView: PrimesPeriodView;
  tamEmail?: string;
  selectedYear?: string;
  referenceDate?: Date;
};

const TAM_MONTHLY_PATH = '/primes/mois';
const TAM_ANNUAL_PATH = '/primes/annuel';
const CLEVEL_ANNUAL_DETAIL_PATH = '/primes/dashboard/details';
const CLEVEL_SYNTHESIS_PATH = '/primes/dashboard';

export type ResolvePrimesPeriodToggleParams = {
  pathname: string;
  searchParams: URLSearchParams;
  isSystem: boolean;
  activeView: PrimesPeriodView;
  tamEmail?: string;
  selectedYear?: string;
  referenceDate?: Date;
};

const isTamSelfServicePrimesRoute = (pathname: string, searchParams: URLSearchParams): boolean => {
  if (parseMonthlyPrimeTamSearchParam(searchParams, true)) {
    return false;
  }

  if (pathname === TAM_MONTHLY_PATH || pathname === TAM_ANNUAL_PATH) {
    return true;
  }

  if (!pathname.startsWith('/primes/')) {
    return false;
  }

  const segment = pathname.slice('/primes/'.length);
  return isValidMonthYear(segment);
};

const isCLevelTamDetailRoute = (pathname: string, searchParams: URLSearchParams, isSystem: boolean): boolean => {
  if (!isSystem) {
    return false;
  }

  if (pathname === CLEVEL_ANNUAL_DETAIL_PATH) {
    return true;
  }

  return parseMonthlyPrimeTamSearchParam(searchParams, true) !== undefined;
};

export const resolvePrimesPeriodToggleProps = ({
  pathname,
  searchParams,
  isSystem,
  activeView,
  tamEmail,
  selectedYear,
  referenceDate,
}: ResolvePrimesPeriodToggleParams): PrimesPeriodToggleResolvedProps | null => {
  if (pathname === CLEVEL_SYNTHESIS_PATH) {
    return null;
  }

  if (isCLevelTamDetailRoute(pathname, searchParams, isSystem)) {
    const trimmedTamEmail = tamEmail?.trim();
    if (!trimmedTamEmail) {
      return null;
    }

    const annualYear = selectedYear ?? format(referenceDate ?? new Date(), 'yyyy');

    return {
      value: activeView,
      monthlyTo: buildCLevelTamMonthlyDetailPath(trimmedTamEmail),
      annualTo: buildCLevelAnnualDetailPath(trimmedTamEmail, annualYear),
    };
  }

  if (isTamSelfServicePrimesRoute(pathname, searchParams)) {
    return {
      value: activeView,
      monthlyTo: TAM_MONTHLY_PATH,
      annualTo: TAM_ANNUAL_PATH,
    };
  }

  return null;
};
